export const LISTING_PAYMENT_RECOVERY_MINIMUM_AGE_MS = 2 * 60 * 1000;
export const LISTING_PAYMENT_POLL_DELAYS_MS = [
  2_000,
  3_000,
  5_000,
  10_000,
] as const;
export const LISTING_PAYMENT_POLL_WINDOW_MS =
  LISTING_PAYMENT_RECOVERY_MINIMUM_AGE_MS;

type TimerHandle = ReturnType<typeof setTimeout>;

type PollingTimerDependencies = {
  now?: () => number;
  setTimer?: (callback: () => void, delayMs: number) => TimerHandle;
  clearTimer?: (timer: TimerHandle) => void;
};

export type ListingPaymentPollingTimer = {
  start(args: { windowMs: number; onTimeout: () => void }): number;
  schedule(run: number, delayMs: number, callback: () => void): boolean;
  isCurrent(run: number): boolean;
  stop(): void;
};

export function createListingPaymentPollingTimer(
  dependencies: PollingTimerDependencies = {}
): ListingPaymentPollingTimer {
  const now = dependencies.now ?? Date.now;
  const setTimer = dependencies.setTimer ?? setTimeout;
  const clearTimer = dependencies.clearTimer ?? clearTimeout;
  let run = 0;
  let running = false;
  let deadlineMs = 0;
  let pollTimer: TimerHandle | null = null;
  let deadlineTimer: TimerHandle | null = null;
  let timeoutCallback: (() => void) | null = null;
  let requestInFlight = false;
  let deadlineReached = false;

  const clearTimers = () => {
    if (pollTimer !== null) {
      clearTimer(pollTimer);
      pollTimer = null;
    }
    if (deadlineTimer !== null) {
      clearTimer(deadlineTimer);
      deadlineTimer = null;
    }
  };

  const stop = () => {
    run += 1;
    running = false;
    timeoutCallback = null;
    requestInFlight = false;
    deadlineReached = false;
    clearTimers();
  };

  const finishAsTimedOut = (expectedRun: number) => {
    if (!running || expectedRun !== run) return;

    deadlineReached = true;
    // A boundary recovery is authoritative even if it settles after the
    // scheduling deadline. Its completion path will stop or reschedule,
    // and rescheduling after the deadline completes as a timeout.
    if (requestInFlight) return;

    const onTimeout = timeoutCallback;
    stop();
    onTimeout?.();
  };

  return {
    start({ windowMs, onTimeout }) {
      stop();
      running = true;
      deadlineMs = now() + windowMs;
      timeoutCallback = onTimeout;
      requestInFlight = false;
      deadlineReached = false;
      const currentRun = run;
      // Boundary polling is eligible for backend recovery. Register timeout
      // one tick later so the poll timer due at deadlineMs starts first.
      deadlineTimer = setTimer(
        () => finishAsTimedOut(currentRun),
        Math.max(0, windowMs) + 1
      );
      return currentRun;
    },

    schedule(expectedRun, delayMs, callback) {
      if (!running || expectedRun !== run) return false;

      requestInFlight = false;
      const remainingMs = deadlineMs - now();
      if (deadlineReached || remainingMs < 0) {
        finishAsTimedOut(expectedRun);
        return false;
      }

      if (pollTimer !== null) {
        clearTimer(pollTimer);
      }
      pollTimer = setTimer(() => {
        pollTimer = null;
        if (!running || expectedRun !== run) return;
        if (now() >= deadlineMs) {
          deadlineReached = true;
        }
        requestInFlight = true;
        callback();
      }, Math.min(Math.max(0, delayMs), remainingMs));
      return true;
    },

    isCurrent(expectedRun) {
      return running && expectedRun === run;
    },

    stop,
  };
}

export async function withPaymentCheckLoading<T>(
  setLoading: (loading: boolean) => void,
  operation: () => Promise<T>
): Promise<T> {
  setLoading(true);
  try {
    return await operation();
  } finally {
    setLoading(false);
  }
}
